import { AfriexTransactionChannel } from '../domain/enums/AfriexTransactionChannel.js';
import { AfriexTransactionType } from '../domain/enums/AfriexTransactionType.js';
import { BUSINESS_FEE_NEGOTIATION_MIN_FRACTION } from '../domain/types/BusinessFeeNegotiation.js';
import { snakeToSentenceCase } from './snakeToSentenceCase.js';
const GROUP_LABELS = {
    COLLECTIONS: 'Collections',
    PAYOUTS: 'Payouts',
};
/** How each group's prices are expressed, stated under the tables. */
const GROUP_DESCRIPTIONS = {
    COLLECTIONS: 'Collection fees are a percentage of the amount.',
    PAYOUTS: 'Payout fees are a flat amount in USD per transaction.',
};
export const FEE_SCHEDULE_TAX_NOTE = 'Fees exclude taxes.';
/** Tag lines for a group's tab, in the order they should read. */
export const describeFeeScheduleGroup = (kind) => [GROUP_DESCRIPTIONS[kind], FEE_SCHEDULE_TAX_NOTE];
/** Channels whose names are acronyms, which sentence case would lower ("Eft"). */
const ACRONYM_CHANNEL_LABELS = {
    [AfriexTransactionChannel.ACH_BANK_ACCOUNT]: 'ACH bank account',
    [AfriexTransactionChannel.EFT]: 'EFT',
    [AfriexTransactionChannel.RFP]: 'RFP',
    [AfriexTransactionChannel.UPI]: 'UPI',
};
/** The channel's human label, so tables are titled identically everywhere. */
export const getFeeScheduleChannelLabel = (channel) => ACRONYM_CHANNEL_LABELS[channel] ??
    snakeToSentenceCase(channel);
/** The group a transaction type belongs to; anything not a deposit is a payout. */
export const getFeeScheduleGroupKind = (type) => type === AfriexTransactionType.DEPOSIT ? 'COLLECTIONS' : 'PAYOUTS';
/** "Collections" or "Payouts", the title of a group. */
export const getFeeScheduleGroupLabel = (kind) => GROUP_LABELS[kind];
/** "Collection" or "Payout", naming the direction of a single corridor. */
export const getCorridorDirectionLabel = (type) => getFeeScheduleGroupKind(type) === 'COLLECTIONS' ? 'Collection' : 'Payout';
/** Rows into Collections and Payouts, then by channel, in insertion order. */
export const groupFeeSchedule = (rows) => {
    const byKind = new Map();
    for (const row of rows) {
        const kind = getFeeScheduleGroupKind(row.type);
        const channels = byKind.get(kind) ?? new Map();
        byKind.set(kind, channels);
        const existing = channels.get(row.channel);
        if (existing) {
            existing.rows.push(row);
        }
        else {
            channels.set(row.channel, {
                channel: row.channel,
                label: getFeeScheduleChannelLabel(row.channel),
                rows: [row],
            });
        }
    }
    const groups = [];
    for (const kind of ['COLLECTIONS', 'PAYOUTS']) {
        const channels = byKind.get(kind);
        if (channels?.size) {
            groups.push({
                kind,
                label: GROUP_LABELS[kind],
                groups: [...channels.values()].map((channel) => ({
                    ...channel,
                    rows: [...channel.rows].sort(compareCorridorsByCountry),
                })),
            });
        }
    }
    return { groups };
};
const trimTrailingZeros = (value) => {
    if (!value.includes('.')) {
        return value;
    }
    return value.replace(/\.?0+$/, '');
};
/** `12` -> "12", `1.50` -> "1.5", so a fee never reads as "1.00%". */
const formatNumber = (value) => trimTrailingZeros(String(value));
/**
 * A fee as the portal states it: percent and flat are added, `valueMax` caps
 * the total ("up to"), no driver reads as "Free". Flat amounts stay in the
 * rule's own currency.
 */
export const formatFeeAmount = (amount) => {
    if (!amount) {
        return '';
    }
    const currency = amount.currencyCode;
    const percent = Number(amount.valuePercent ?? 0);
    const fixed = Number(amount.valueFixed ?? 0);
    const max = Number(amount.valueMax ?? 0);
    const money = (value) => currency ? `${currency} ${value.toFixed(2)}` : value.toFixed(2);
    const drivers = [];
    if (percent) {
        drivers.push(`${formatNumber(percent)}%`);
    }
    if (fixed) {
        drivers.push(money(fixed));
    }
    if (!drivers.length) {
        // A cap with no driver is not a fee either; the engine ignores it.
        return 'Free';
    }
    const fee = drivers.join(' + ');
    return max ? `${fee} (up to ${money(max)})` : fee;
};
/** The one key for a corridor, shared by ticks, drafts and round diffs. */
export const getCorridorKey = ({ type, channel, currencyCode, countryCode, }) => [type, channel, currencyCode, countryCode].join('|');
/** Same proposal when all three drivers match. */
export const isSameFeeAmount = (a, b) => Number(a?.valuePercent ?? 0) === Number(b?.valuePercent ?? 0) &&
    Number(a?.valueFixed ?? 0) === Number(b?.valueFixed ?? 0) &&
    Number(a?.valueMax ?? 0) === Number(b?.valueMax ?? 0);
/** Diffs the standing round against the one before it. */
export const diffNegotiationRounds = (rounds) => {
    const current = rounds[rounds.length - 1];
    if (!current) {
        return [];
    }
    const previous = new Map((rounds[rounds.length - 2]?.lines ?? []).map((line) => [
        getCorridorKey(line.corridor),
        line,
    ]));
    return current.lines.map((line) => {
        const previousLine = previous.get(getCorridorKey(line.corridor));
        return {
            corridor: line.corridor,
            proposedAmount: line.proposedAmount,
            previousAmount: previousLine?.proposedAmount,
            defaultAmount: line.defaultAmount,
            monthlyVolumeUsd: line.monthlyVolumeUsd,
            isChanged: previousLine
                ? !isSameFeeAmount(previousLine.proposedAmount, line.proposedAmount)
                : false,
            isOpeningRound: !previousLine,
        };
    });
};
/**
 * Whether a fee is quoted as a percentage or a flat amount. A rule can carry
 * both; the percentage leads.
 */
export const getFeeAmountKind = (amount) => Number(amount?.valuePercent ?? 0) ? 'percent' : 'flat';
/** The editable driver of a fee, as text for an input. */
export const feeAmountToInputText = (amount) => {
    const value = getFeeAmountKind(amount) === 'percent'
        ? amount?.valuePercent
        : amount?.valueFixed;
    return value === undefined || value === null ? '' : String(value);
};
const parseFeeInput = (text) => {
    const trimmed = text.trim();
    if (!trimmed) {
        return undefined;
    }
    const value = Number(trimmed);
    return Number.isFinite(value) && value >= 0 ? value : undefined;
};
/**
 * A proposal built from typed text, in the same kind as the live fee. Only the
 * leading driver changes; currency, cap and any second driver are kept, so a
 * percentage corridor can never be proposed as a flat one.
 */
export const buildProposedFeeAmount = (text, liveAmount) => {
    const value = parseFeeInput(text);
    if (value === undefined) {
        return undefined;
    }
    const base = {
        ...(liveAmount?.currencyCode
            ? { currencyCode: liveAmount.currencyCode }
            : {}),
        ...(liveAmount?.valueFixed ? { valueFixed: liveAmount.valueFixed } : {}),
        ...(liveAmount?.valueMax ? { valueMax: liveAmount.valueMax } : {}),
        ...(liveAmount?.valuePercent
            ? { valuePercent: liveAmount.valuePercent }
            : {}),
    };
    if (getFeeAmountKind(liveAmount) === 'percent') {
        return value > 100 ? undefined : { ...base, valuePercent: value };
    }
    return { ...base, valueFixed: String(value) };
};
/**
 * Why a proposal cannot price this corridor, or undefined when it can. It must
 * keep the live fee's kind, currency, cap and second driver.
 */
export const getProposedFeeAmountError = (proposed, liveAmount) => {
    const isPercent = getFeeAmountKind(liveAmount) === 'percent';
    const rebuilt = buildProposedFeeAmount(String((isPercent ? proposed?.valuePercent : proposed?.valueFixed) ?? ''), liveAmount);
    if (!rebuilt) {
        return isPercent
            ? 'Enter a percentage between 0 and 100.'
            : 'Enter a flat amount of 0 or more.';
    }
    return isSameFeeAmount(rebuilt, proposed) &&
        (rebuilt.currencyCode ?? '') === (proposed?.currencyCode ?? '')
        ? undefined
        : 'Proposed pricing must keep the current fee type and currency.';
};
/**
 * The editable driver of a fee as a number: the percent, or the flat value.
 * `kindOf` is the fee whose kind decides which: a proposal of 0% has no
 * percent to lead with, so it is read by the live fee's kind.
 */
const feeValue = (amount, kindOf = amount) => {
    const raw = getFeeAmountKind(kindOf) === 'percent'
        ? amount?.valuePercent
        : amount?.valueFixed;
    const value = Number(raw);
    return raw === undefined || raw === '' || !Number.isFinite(value)
        ? undefined
        : value;
};
/** How far below the current price a proposal sits, in whole percent. */
export const getFeeReductionPercent = (proposed, current) => {
    const asked = feeValue(proposed, current);
    const now = feeValue(current);
    return asked === undefined || !now
        ? undefined
        : Math.round((1 - asked / now) * 100);
};
/**
 * Why a business's proposal cannot go: not lower than what it pays now, or
 * below the share of the corridor's default it may ask for.
 */
export const getProposedFeeFloorError = (proposed, current, defaultAmount) => {
    const asked = feeValue(proposed, current ?? defaultAmount);
    if (asked === undefined) {
        return undefined;
    }
    const now = feeValue(current);
    if (now !== undefined && asked >= now) {
        return 'Ask for less than your current rate.';
    }
    const base = feeValue(defaultAmount);
    if (base === undefined) {
        return undefined;
    }
    // Rounded up to the cent or hundredth of a percent the inputs accept. The
    // product is first rounded to 6 places so float noise (1.85 * 0.6 * 100 is
    // 111.00000000000001) does not push the floor up a cent.
    const floor = Math.ceil(Number((base * BUSINESS_FEE_NEGOTIATION_MIN_FRACTION * 100).toFixed(6))) / 100;
    if (asked >= floor) {
        return undefined;
    }
    const shown = getFeeAmountKind(defaultAmount) === 'percent'
        ? `${formatNumber(floor)}%`
        : `${defaultAmount?.currencyCode ?? ''} ${floor.toFixed(2)}`.trim();
    return `The lowest you can ask for on this corridor is ${shown}.`;
};
const regionNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : undefined;
/** "NG" to "Nigeria". Falls back to the code. */
export const getCountryName = (countryCode) => {
    try {
        return regionNames?.of(countryCode) ?? countryCode;
    }
    catch {
        return countryCode;
    }
};
/** "NG" to its flag emoji; empty for anything that is not two letters. */
export const getCountryFlag = (countryCode) => {
    if (!/^[A-Za-z]{2}$/.test(countryCode)) {
        return '';
    }
    const code = countryCode.toUpperCase();
    const regional = (index) => 0x1f1e6 + code.charCodeAt(index) - 65;
    return String.fromCodePoint(regional(0), regional(1));
};
/** Orders corridors by country name, then currency, so every table reads A to Z. */
export const compareCorridorsByCountry = (a, b) => getCountryName(a.countryCode).localeCompare(getCountryName(b.countryCode)) ||
    a.currencyCode.localeCompare(b.currencyCode);
/** A sorted copy of `items`, by the corridor `getCorridor` reads off each one. */
export const sortByCountry = (items, getCorridor) => [...items].sort((a, b) => compareCorridorsByCountry(getCorridor(a), getCorridor(b)));
//# sourceMappingURL=feeScheduleUtils.js.map